import React, { useState, useEffect } from "react";
import { Button, Select, Spin } from "antd";
import { createSubProject, updateException, getAllSubProjects, getAllSubProjectTypes } from "../api";
import { message } from "antd";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const SubProjectComponent = ({ exception, onClose, onResolved }) => {
    const [subProjectName, setSubProjectName] = useState(null);
    const [procuringEntityPackageId, setProcuringEntityPackageId] = useState(null);
    const [procuringEntityId, setProcuringEntityId] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [subProjects, setSubProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [subProjectTypes, setSubProjectTypes] = useState([]);
    const [selectedSubProjectTypeId, setSelectedSubProjectTypeId] = useState(null);

    useEffect(() => {
        const extractSubProjectDetails = () => {
            setSubProjectName(exception?.unknown_infomation);
            setProcuringEntityPackageId(exception.procuring_entity_package_id);
            setProcuringEntityId(exception.procuring_entity_id);
            setProjectId(exception.project_id);
        };

        if (exception) {
            extractSubProjectDetails();
        }

        const fetchSubProjects = async () => {
            try {
                const subProjectsAll = (await getAllSubProjects(exception?.procuring_entity_package_id)).data;
                setSubProjects(Array.isArray(subProjectsAll) ? subProjectsAll : []);
            } catch (error) {
                console.error("Failed to fetch sub-projects:", error);
            }
        };

        const fetchSubProjectTypes = async () => {
            try {
                const response = await getAllSubProjectTypes();
                setSubProjectTypes(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error("Failed to fetch sub-project types:", error);
            }
        };

        fetchSubProjects();
        fetchSubProjectTypes();
    }, [exception]);

    const handleMarkAsNew = async () => {
        if (!selectedSubProjectTypeId) {
            message.warning('Please select a sub-project type');
            return;
        }

        if (subProjectName && procuringEntityPackageId && procuringEntityId && projectId) {
            if (exception.status === 'pending') {
                try {
                    setLoading(true);
                    await createSubProject({
                        name: subProjectName,
                        procuring_entity_package_id: procuringEntityPackageId,
                        procuring_entity_id: procuringEntityId,
                        project_id: projectId,
                        sub_project_type_id: selectedSubProjectTypeId
                    });
                    await updateException(exception.id);
                    onClose();
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    message.success('Sub-project created');
                    setLoading(false);
                    console.log(`Sub-Project "${subProjectName}" created successfully.`);
                } catch (error) {
                    console.error(`Failed to create sub-project "${subProjectName}".`, error);
                    setLoading(false);
                    onClose();
                    message.error('Failed to create sub-project');
                }
            } else {
                console.warn("Exception already resolved.");
                setLoading(false);
                onClose();
            }
        } else {
            console.warn("Incomplete sub-project details.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleSubProjectSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        
        if (!selectedSubProjectTypeId) {
            message.warning('Please select a sub-project type');
            return;
        }

        if (exception.status === 'pending') {
            try {
                setLoading(true);
                await createSubProject({
                    name: subProjectName,
                    procuring_entity_package_id: procuringEntityPackageId,
                    procuring_entity_id: procuringEntityId,
                    project_id: projectId,
                    alias_for: selectedAliasId,
                    sub_project_type_id: selectedSubProjectTypeId
                });
                await updateException(exception.id);
                setLoading(false);
                onClose();
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                message.success('Alias saved');
                console.log(`Sub-Project "${subProjectName}" set as alias for sub-project with ID: ${selectedAliasId}`);
            } catch (error) {
                console.error(`Failed to update alias for sub-project with ID: ${selectedAliasId}`, error);
                setLoading(false);
                onClose();
                message.error('Failed to save alias');
            }
        } else {
            console.warn("Exception already resolved.");
            setLoading(false);
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                            Select Sub-Project Type <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Select
                            style={{ width: '100%', maxWidth: 400 }}
                            placeholder="Select a sub-project type"
                            onChange={(value) => setSelectedSubProjectTypeId(value)}
                            value={selectedSubProjectTypeId ?? undefined}
                            disabled={loading}
                        >
                            {subProjectTypes?.map((type) => (
                                <Option key={type.id} value={type.id}>
                                    {type.name}
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                            type="primary"
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button
                            type="default"
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {subProjectName ? `Select an existing sub-project to alias "${subProjectName}" to.` : 'Select an existing sub-project to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a sub-project"
                                    onChange={handleSubProjectSelect}
                                    value={selectedAliasId ?? undefined}
                                    disabled={loading}
                                >
                                    {subProjects?.map((subProject) => (
                                        <Option key={subProject.id} value={subProject.id}>
                                            {subProject.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default SubProjectComponent;
